import {
	useEffect, useMemo, useRef, useState
} from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { menuData } from 'data/menu/menuData';
import { MenuCategoryNav } from './menuCategoryNav';
import { MenuCategorySection } from './menuCategorySection';
import { MenuItemCarouselModal } from './menuItemCarouselModal';
import Styles from './menu.module.scss';

gsap.registerPlugin(ScrollTrigger);

const { categories } = menuData;
const allItems = categories.flatMap(
	(category) => category.sections.flatMap((section) => section.items)
);

const ITEM_MODAL_ENABLED = true;
const ITEM_REVEAL_DISTANCE = 240;
const itemRevealEase = gsap.parseEase('back.out(1.6)');

const Menu = () => {
	const sectionRef = useRef(null);
	const contentRef = useRef(null);
	const categoryRefs = useRef({});
	const itemRefs = useRef({});
	const [activeId, setActiveId] = useState(categories[0]?.id);
	const [open, setOpen] = useState(false);
	const [startIndex, setStartIndex] = useState(0);

	useEffect(() => {
		const mm = gsap.matchMedia();

		mm.add('(min-width: 993px)', () => {
			const section = sectionRef.current;
			const content = contentRef.current;

			if (!section || !content) return undefined;

			const getDistance = () => Math.max(content.scrollHeight - section.clientHeight, 0);

			const trigger = ScrollTrigger.create({
				trigger: section,
				start: 'top top',
				end: () => `+=${getDistance()}`,
				pin: true,
				anticipatePin: 1,
				invalidateOnRefresh: true,
				onUpdate: (self) => {
					const distance = getDistance();
					const offset = self.progress * distance;

					gsap.set(content, { y: -offset });

					const center = offset + section.clientHeight / 2;
					let current = categories[0]?.id;

					Object.entries(categoryRefs.current).forEach(([id, el]) => {
						if (el && el.offsetTop <= center) current = id;
					});

					setActiveId((prev) => (prev === current ? prev : current));
				}
			});

			return () => {
				gsap.set(content, { y: 0 });
				trigger.kill();
			};
		});

		return () => mm.revert();
	}, []);

	useEffect(() => {
		let lastScrollY = null;

		const revealItems = () => {
			if (window.scrollY === lastScrollY) return;
			lastScrollY = window.scrollY;

			const viewportHeight = window.innerHeight;

			Object.values(itemRefs.current).forEach((el) => {
				if (!el) return;

				const { top } = el.getBoundingClientRect();
				const raw = (viewportHeight - top) / ITEM_REVEAL_DISTANCE;
				const progress = itemRevealEase(Math.min(Math.max(raw, 0), 1));

				gsap.set(el, {
					opacity: Math.min(progress, 1),
					y: (1 - progress) * 46,
					scale: 0.94 + progress * 0.06
				});
			});
		};

		gsap.ticker.add(revealItems);

		return () => gsap.ticker.remove(revealItems);
	}, []);

	useEffect(() => {
		if (window.scrollY > 100) return undefined;

		const nudge = Math.min(280, window.innerHeight * 0.28);
		const timeout = setTimeout(() => {
			window.scrollTo({ top: nudge, behavior: 'smooth' });
		}, 300);

		return () => clearTimeout(timeout);
	}, []);

	const activeLabel = useMemo(
		() => categories.find((category) => category.id === activeId)?.label ?? '',
		[activeId]
	);

	const handleItemClick = (items, index) => {
		const clickedItem = items[index];
		const globalIndex = allItems.indexOf(clickedItem);

		setStartIndex(globalIndex);

		if (ITEM_MODAL_ENABLED) setOpen(true);
	};

	const handleClose = () => setOpen(false);

	return (
		<div className={Styles.menuWrapper}>
			<section ref={sectionRef} className={Styles.menuSection}>
				<motion.div
					className={Styles.menuInner}
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					transition={{ duration: 0.6, ease: 'easeOut' }}
				>
					<MenuCategoryNav label={activeLabel} />
					<div ref={contentRef} className={Styles.content}>
						{categories.map((category) => (
							<MenuCategorySection
								key={category.id}
								category={category}
								sectionRef={(el) => { categoryRefs.current[category.id] = el; }}
								itemRef={(categoryId, sectionIndex, index, el) => {
									itemRefs.current[`${categoryId}-${sectionIndex}-${index}`] = el;
								}}
								onItemClick={handleItemClick}
							/>
						))}
					</div>
				</motion.div>
				<AnimatePresence>
					{open && (
						<MenuItemCarouselModal
							key="carousel-modal"
							onClose={handleClose}
							items={allItems}
							startIndex={startIndex}
							setStartIndex={setStartIndex}
						/>
					)}
				</AnimatePresence>
			</section>
		</div>
	);
};

export default Menu;
